import { fork, take, put, select, cancel, delay, call } from 'redux-saga/effects';
import { parseInt } from 'lodash';
import { addDays, isBefore } from 'date-fns';
import { loadSubTm, saveSubTm } from '@ts/utils/cs';
import { AnalyticsInstance } from '@ts/utils/analytics';
import {
    userUpdate,
    trackSubscribed,
    trackUpdateCart,
    convCartItemsToPurchaseItems,
} from '@ts/utils/iterableUtils';

import actions from '../actions';
import selectors from '../selectors';

import { setContactInfoToLS } from './checkout';

const isDev = process.env.NODE_ENV === 'development';

export function* onSubmit() {
    try {
        const email = yield select(selectors.subscribe.makeSelectEmail());
        if (!email) {
            throw new Error('Please enter e-mail address');
        }

        if (!/^.+@.+\..+$/g.test(email)) {
            throw new Error('Please enter valid e-mail address');
        }

        yield put(actions.subscribe.changeIsError(false));
        yield put(actions.subscribe.changeIsLoading(true));

        if (!window._iaq.user || !window._iaq.user.email || window._iaq.user.email !== email) {
            const contactInfo = yield select(selectors.checkout.makeSelectContactInfo());
            const userId = yield select(selectors.checkout.makeSelectUserId());

            const response = yield userUpdate(email, {
                firstName: contactInfo.firstName,
                lastName: contactInfo.lastName,
                fullName: [contactInfo.firstName, contactInfo.lastName].filter(Boolean).join(' '),
                phoneNumber: contactInfo.phone,
            });
            if (response.statusCode !== 200) {
                if (
                    response.data &&
                    typeof response.data === 'object' &&
                    typeof response.data.msg === 'string'
                ) {
                    throw new Error(response.data.msg);
                } else {
                    throw new Error('Sorry, something is wrong');
                }
            }

            try {
                const cartItems = yield select(selectors.cart.makeSelectItems());
                trackUpdateCart(
                    convCartItemsToPurchaseItems(
                        cartItems.map((item) => ({
                            ...item,
                            trip_name: item.product_name,
                            activity: { id: item.product_id, slug: item.product_slug },
                        }))
                    )
                );
            } catch {}

            // save data to LS
            yield put(actions.checkout.changeContactInfoEmail(email));
            yield call(setContactInfoToLS, {
                userId,
                email,
                firstName: contactInfo.firstName,
                lastName: contactInfo.lastName,
                phone: contactInfo.phone,
                secondaryPhone: contactInfo.secondaryPhone,
            });
        }

        // try subscribe
        const subres = yield trackSubscribed({ email });
        if (subres.statusCode !== 200) {
            throw new Error('Something is wrong');
        }

        if (isDev) {
            // loading imitation on dev env
            yield delay(1500);
        }

        saveSubTm(new Date().getTime());
        yield put(actions.subscribe.changeIsAlready());
        AnalyticsInstance.account.subscribe({ email });
    } catch (err) {
        yield put(actions.subscribe.changeErrorMessage(err.message));
        yield put(actions.subscribe.changeIsError(true));
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.subscribe.changeIsLoading(false));
    }
}

export function* watchSubmit() {
    let task;
    while (true) {
        yield take(actions.subscribe.ActionTypes.SUBMIT);

        if (task) {
            yield cancel(task);
            task = undefined;
        }

        task = yield fork(onSubmit);
    }
}

export default function* subscribeSaga() {
    yield fork(watchSubmit);

    const tmBeforeParse = yield loadSubTm();
    const tm = parseInt(tmBeforeParse);
    if (isFinite(tm)) {
        const m = yield new Date(tm * 1000);
        if (m && isBefore(addDays(new Date(), -30), m)) {
            yield put(actions.subscribe.changeIsAlready(true));
        }
    }
}
